import axios from 'axios';
import { LoginRequest, RegisterRequest, ApiResponse, User, AccountResponse, FormattedAuditLog } from '../types';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'https://www.thetraders.ai/api/v1';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  // Clean up old token format if it exists
  const oldTokens = localStorage.getItem('auth_tokens');
  if (oldTokens) {
    console.log('Migrating from old token format...');
    localStorage.removeItem('auth_tokens');
  }
  
  const storedToken = localStorage.getItem('auth_token');
  if (storedToken) {
    try {
      config.headers.Authorization = `Bearer ${storedToken}`;
    } catch (error) {
      console.warn('Failed to use stored token:', error);
      localStorage.removeItem('auth_token');
    }
  }
  return config;
});

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    // Handle undefined or empty responses
    if (!response.data) {
      console.warn('API response data is undefined or empty:', response);
      response.data = { success: false, data: null, error: 'Empty response' };
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't redirect on 409 conflicts - let registration handle them with modal
    // Don't redirect on login/register/me endpoints - let them handle their own errors
    if (error.response?.status === 401 && !originalRequest._retry && 
        !originalRequest.url?.includes('/auth/login') && 
        !originalRequest.url?.includes('/auth/register') &&
        !originalRequest.url?.includes('/auth/me')) {
      originalRequest._retry = true;

      try {
        // Clean up both old and new token formats
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_tokens');
        console.log('Authentication failed, redirecting to login...');
        window.location.href = '/auth';
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_tokens');
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const login = async (credentials: LoginRequest): Promise<ApiResponse<{ user: User; token: string; automatedTrading: any }>> => {
  try {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  } catch (error: any) {
    // Handle login-specific errors without redirecting
    if (error.response?.status === 401) {
      return {
        success: false,
        data: null as any,
        error: 'Invalid email or password. Please check your credentials and try again.'
      };
    }
    if (error.response?.status === 400) {
      return {
        success: false,
        data: null as any,
        error: error.response.data?.error || 'Invalid request. Please check your input.'
      };
    }
    if (error.response?.status >= 500) {
      return {
        success: false,
        data: null as any,
        error: 'Server error. Please try again later.'
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const register = async (userData: RegisterRequest): Promise<ApiResponse<{ user: User; token: string; automatedTrading: any }>> => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error: any) {
    // Handle 409 Conflict (user already exists) with better error message
    if (error.response?.status === 409) {
      // Return error response that can be handled by AuthContext
      return {
        success: false,
        data: null as any,
        error: 'This email address is already registered. Please check your email for a verification link, or try signing in instead.'
      };
    }
    
    // Handle 500 errors with better messaging
    if (error.response?.status === 500) {
      const errorMessage = error.response?.data?.error || 'Server error. Please try again later.';
      return {
        success: false,
        data: null as any,
        error: errorMessage
      };
    }
    
    // For other errors, return a formatted error response
    const errorMessage = error.response?.data?.error || error.message || 'Registration failed. Please try again.';
    return {
      success: false,
      data: null as any,
      error: errorMessage
    };
  }
};

export const logout = async (): Promise<void> => {
  await api.post('/auth/logout');
  localStorage.removeItem('auth_token');
};

export const getCurrentUser = async (): Promise<ApiResponse<User>> => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const changePassword = async (passwordData: { email: string; currentPassword: string; newPassword: string }): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.put('/auth/change-password', passwordData);
  return response.data;
};

export const requestPasswordReset = async (email: string): Promise<ApiResponse<{ message: string }>> => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error: any) {
    // Handle password reset specific errors
    if (error.response?.status === 404) {
      return {
        success: false,
        data: null as any,
        error: 'No account found with this email address.'
      };
    }
    if (error.response?.status === 429) {
      return {
        success: false,
        data: null as any,
        error: 'Too many reset requests. Please try again later.'
      };
    }
    if (error.response?.status >= 500) {
      return {
        success: false,
        data: null as any,
        error: 'Server error. Please try again later.'
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const resetPassword = async (token: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
  try {
    const response = await api.post('/auth/reset-password', { 
      token, 
      newPassword: newPassword 
    });
    return response.data;
  } catch (error: any) {
    // Handle password reset specific errors
    if (error.response?.status === 400) {
      return {
        success: false,
        data: null as any,
        error: error.response.data?.error || 'Invalid or expired reset token.'
      };
    }
    if (error.response?.status === 404) {
      return {
        success: false,
        data: null as any,
        error: 'Reset token not found or has expired.'
      };
    }
    if (error.response?.status >= 500) {
      return {
        success: false,
        data: null as any,
        error: 'Server error. Please try again later.'
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const deleteUserAccount = async (): Promise<ApiResponse<{ message: string }>> => {
  try {
    const response = await api.delete('/auth/me');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 403) {
      return {
        success: false,
        data: null as any,
        error: error.response.data?.error || 'Admin accounts cannot be deleted.'
      };
    }
    if (error.response?.status === 401) {
      return {
        success: false,
        data: null as any,
        error: 'You must be logged in to delete your account.'
      };
    }
    if (error.response?.status >= 500) {
      return {
        success: false,
        data: null as any,
        error: 'Server error. Please try again later.'
      };
    }
    throw error;
  }
};

export const deleteUserByAdmin = async (userId: string): Promise<ApiResponse<{ message: string; deletedUserId: string }>> => {
  try {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 403) {
      return {
        success: false,
        data: null as any,
        error: error.response.data?.error || 'Cannot delete admin accounts or your own account.'
      };
    }
    if (error.response?.status === 404) {
      return {
        success: false,
        data: null as any,
        error: 'User not found.'
      };
    }
    if (error.response?.status >= 500) {
      return {
        success: false,
        data: null as any,
        error: 'Server error. Please try again later.'
      };
    }
    throw error;
  }
};

// Accounts API
export const getAccounts = async (): Promise<ApiResponse<AccountResponse[]>> => {
  const response = await api.get('/accounts');
  return response.data;
};

export const createAccount = async (accountData: any): Promise<ApiResponse<AccountResponse>> => {
  const response = await api.post('/accounts', accountData);
  return response.data;
};

export const deleteAccount = async (accountId: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/accounts/${accountId}`);
  return response.data;
};

// Trades API
export const getTrades = async (params?: {
  limit?: number;
  offset?: number;
  symbol?: string;
  side?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  accountId?: string;
}): Promise<ApiResponse<{ trades: any[]; summary: any; pagination: any }>> => {
  try {
    const response = await api.get('/trades', { params });
    if (!response.data) {
      return { success: false, data: { trades: [], summary: null, pagination: null }, error: 'No trades data received' };
    }
    
    // Handle both response structures
    if (response.data.success && response.data.data) {
      // Backend returns { success: true, data: { trades: [], ... } }
      return {
        success: true,
        data: response.data.data
      };
    }
    
    // Fallback to direct response.data if structure is different
    return response.data;
  } catch (error: any) {
    console.error('Trades API error:', error);
    
    // Handle validation errors with details
    if (error?.response?.status === 400 && error?.response?.data?.error?.details) {
      const details = error.response.data.error.details;
      const validationMessages = Array.isArray(details) 
        ? details.map((d: any) => `${d.path?.join('.') || 'field'}: ${d.message}`).join(', ')
        : 'Invalid query parameters';
      return { 
        success: false, 
        data: { trades: [], summary: null, pagination: null }, 
        error: `Validation error: ${validationMessages}` 
      };
    }
    
    // Handle other error formats
    const errorMessage = error?.response?.data?.error?.message 
      || error?.response?.data?.error 
      || error?.message 
      || 'Failed to fetch trades';
    return { success: false, data: { trades: [], summary: null, pagination: null }, error: errorMessage };
  }
};

// Admin API - Get all trades across all users
export const getAllTrades = async (params?: {
  limit?: number;
  offset?: number;
  symbol?: string;
  side?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}): Promise<ApiResponse<{ trades: any[]; summary: any; pagination: any }>> => {
  try {
    const response = await api.get('/admin/all-trades', { params });
    if (!response.data) {
      return { success: false, data: { trades: [], summary: null, pagination: null }, error: 'No trades data received' };
    }
    
    // Handle both response structures
    if (response.data.success && response.data.data) {
      // Backend returns { success: true, data: { trades: [], ... } }
      return {
        success: true,
        data: response.data.data
      };
    }
    
    // Fallback to direct response.data if structure is different
    return response.data;
  } catch (error: any) {
    console.error('Admin trades API error:', error);
    
    // Handle validation errors with details
    if (error?.response?.status === 400 && error?.response?.data?.error?.details) {
      const details = error.response.data.error.details;
      const validationMessages = Array.isArray(details) 
        ? details.map((d: any) => `${d.path?.join('.') || 'field'}: ${d.message}`).join(', ')
        : 'Invalid query parameters';
      return { 
        success: false, 
        data: { trades: [], summary: null, pagination: null }, 
        error: `Validation error: ${validationMessages}` 
      };
    }
    
    // Handle other error formats
    const errorMessage = error?.response?.data?.error?.message 
      || error?.response?.data?.error 
      || error?.message 
      || 'Failed to fetch all trades';
    return { success: false, data: { trades: [], summary: null, pagination: null }, error: errorMessage };
  }
};

// User API - Get user's own audit logs
export const getUserAuditLogs = async (params?: {
  limit?: number;
  offset?: number;
  eventType?: string;
  startDate?: string;
  endDate?: string;
}): Promise<ApiResponse<{ auditLogs: FormattedAuditLog[]; summary: any; pagination: any }>> => {
  try {
    const response = await api.get('/logs/audit', { params });
    if (!response.data) {
      return { success: false, data: { auditLogs: [], summary: null, pagination: null }, error: 'No audit logs data received' };
    }
    return response.data;
  } catch (error) {
    console.error('User audit logs API error:', error);
    return { success: false, data: { auditLogs: [], summary: null, pagination: null }, error: error instanceof Error ? error.message : 'Failed to fetch user audit logs' };
  }
};

// Admin API - Get all audit logs across all users
export const getAllAuditLogs = async (params?: {
  limit?: number;
  offset?: number;
  eventType?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
}): Promise<ApiResponse<{ auditLogs: FormattedAuditLog[]; summary: any; pagination: any }>> => {
  try {
    const response = await api.get('/admin/all-audit-logs', { params });
    if (!response.data) {
      return { success: false, data: { auditLogs: [], summary: null, pagination: null }, error: 'No audit logs data received' };
    }
    return response.data;
  } catch (error) {
    console.error('Admin audit logs API error:', error);
    return { success: false, data: { auditLogs: [], summary: null, pagination: null }, error: error instanceof Error ? error.message : 'Failed to fetch all audit logs' };
  }
};

// Portfolio API
export const getPortfolio = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/portfolio/summary');
    if (!response.data) {
      return { success: false, data: null, error: 'No portfolio data received' };
    }
    return response.data;
  } catch (error) {
    console.error('Portfolio API error:', error);
    return { success: false, data: null, error: 'Failed to fetch portfolio' };
  }
};

export const getPortfolioPositions = async (): Promise<ApiResponse<any[]>> => {
  try {
    const response = await api.get('/portfolio/positions');
    if (!response.data) {
      return { success: false, data: [], error: 'No positions data received' };
    }
    return response.data;
  } catch (error) {
    console.error('Portfolio positions API error:', error);
    return { success: false, data: [], error: 'Failed to fetch positions' };
  }
};

export const getPortfolioHistory = async (): Promise<ApiResponse<any[]>> => {
  const response = await api.get('/portfolio/history');
  return response.data;
};

export const getPortfolioPerformance = async (): Promise<ApiResponse<any>> => {
  const response = await api.get('/portfolio/performance');
  return response.data;
};

// Trading API
export const getTradingMetrics = async (): Promise<ApiResponse<any>> => {
  const response = await api.get('/trading/metrics');
  return response.data;
};

export const getTradingState = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/trading/status');
    if (!response.data) {
      return { success: false, data: { status: 'UNKNOWN' }, error: 'No trading status received' };
    }
    return response.data;
  } catch (error) {
    console.error('Trading status API error:', error);
    return { success: false, data: { status: 'UNKNOWN' }, error: 'Failed to fetch trading status' };
  }
};

export const getTailLogs = async (limit: number = 200): Promise<ApiResponse<{ count: number; logs: any[] }>> => {
  const response = await api.get(`/logs/tail?limit=${limit}`);
  return response.data;
};

export const getDailyReview = async (): Promise<ApiResponse<{ reviewed: number; executed: number }>> => {
  const response = await api.get('/logs/daily-review');
  return response.data;
};

// Preferences API
export const getPDTStatus = async (accountId?: string): Promise<ApiResponse<{
  isPDTQualified: boolean;
  equity: number;
  minRequired: number;
  accountId: string | null;
  accountName: string | null;
  statusText: string;
  error?: string;
} | null>> => {
  try {
    const url = accountId 
      ? `/preferences/pdt-status?accountId=${accountId}`
      : '/preferences/pdt-status';
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    console.error('Error getting PDT status:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get PDT status',
      data: null,
    };
  }
};

export const getTradingPreferences = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/preferences/trading');
    return response.data;
  } catch (error: any) {
    // Handle 404 - user doesn't have preferences yet (expected for new users)
    if (error.response?.status === 404) {
      return {
        success: false,
        data: null,
        error: 'Trading preferences not found - will use defaults'
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const updateTradingPreferences = async (preferences: any): Promise<ApiResponse<any>> => {
  try {
    console.log('API: Making PUT request to /preferences/trading with data:', preferences);
    console.log('API: Base URL:', API_BASE_URL);
    console.log('API: Full URL will be:', `${API_BASE_URL}/preferences/trading`);
    
    // Add cache-busting and ensure no caching
    const response = await api.put('/preferences/trading', preferences, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    console.log('API: Raw response status:', response.status);
    console.log('API: Raw response headers:', response.headers);
    console.log('API: Raw response data:', response.data);
    
    return response.data;
  } catch (error: any) {
    console.error('API: PUT request failed with error:', error);
    console.error('API: Error details:', {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
      config: error?.config
    });
    throw error;
  }
};

export const getNotificationPreferences = async (): Promise<ApiResponse<any>> => {
  const response = await api.get('/preferences/notifications');
  return response.data;
};

export const updateNotificationPreferences = async (preferences: any): Promise<ApiResponse<any>> => {
  const response = await api.put('/preferences/notifications', preferences);
  return response.data;
};

// Profile API
export const getProfile = async (): Promise<ApiResponse<any>> => {
  const response = await api.get('/profile');
  return response.data;
};

export const updateProfile = async (profileData: {
  firstName?: string;
  lastName?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHERS' | 'PREFER_NOT_TO_SAY' | null;
  address?: string | null;
  bio?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  country?: string | null;
  timezone?: string | null;
}): Promise<ApiResponse<any>> => {
  const response = await api.put('/profile', profileData);
  return response.data;
};

export default api;
